import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    TextField
} from '@mui/material'
import {
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material'
import GooglePlacesAutocomplete from './GooglePlacesAutoComplete'
import GoogleSignInButton from './GoogleSignInButton'
import { useAuth } from '../hooks/useAuth'
import { GoogleAuthService } from '../services/GoogleAuthService'
import axiosClient from '../services/axiosClient'

const STORAGE_KEY = 'householdOnboardingFormData'
const STEP_KEY = 'householdOnboardingStep'

interface HouseholdFormData {
    householdAddress: string
    householdNickname: string
    venmoHandle: string
}

type StepId = 'address' | 'nickname' | 'venmo' | 'finish'

const STEPS: { id: StepId; title: string; subtext?: string }[] = [
    { id: 'address', title: 'Household address?' },
    {
        id: 'nickname',
        title: 'Give your household a nickname',
        subtext: "Optional — we'll generate a fun one if you skip this."
    },
    {
        id: 'venmo',
        title: "What's your Venmo username?",
        subtext: 'Optional — so roommates can pay you back.'
    },
    { id: 'finish', title: '' }
]

function loadSavedForm(): HouseholdFormData {
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            const parsed = JSON.parse(saved)
            return {
                householdAddress: parsed.householdAddress || '',
                householdNickname: parsed.householdNickname || '',
                venmoHandle: parsed.venmoHandle || ''
            }
        }
    } catch {
        // ignore corrupt storage
    }
    return { householdAddress: '', householdNickname: '', venmoHandle: '' }
}

function loadSavedStep(): number {
    try {
        const saved = localStorage.getItem(STEP_KEY)
        if (saved != null) {
            const step = parseInt(saved, 10)
            if (!Number.isNaN(step) && step >= 0 && step < STEPS.length) return step
        }
    } catch {
        // ignore
    }
    return 0
}

function clearSavedOnboarding() {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STEP_KEY)
}

interface HouseholdOnboardingFormProps {
    isVisible: boolean
    id?: string
}

export default function HouseholdOnboardingForm({ isVisible, id }: HouseholdOnboardingFormProps) {
    const { user } = useAuth()
    const [currentStep, setCurrentStep] = useState(loadSavedStep)
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [submitting, setSubmitting] = useState(false)
    const [formData, setFormData] = useState<HouseholdFormData>(loadSavedForm)

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData))
    }, [formData])

    useEffect(() => {
        localStorage.setItem(STEP_KEY, currentStep.toString())
    }, [currentStep])

    const step = STEPS[currentStep]

    const handleInputChange = (field: keyof HouseholdFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }

    const validateCurrentStep = () => {
        if (step.id === 'address') {
            if (!formData.householdAddress.trim()) {
                setErrors(prev => ({ ...prev, householdAddress: 'Address is required' }))
                return false
            }
        }
        return true
    }

    const handleNext = () => {
        if (!validateCurrentStep()) return
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1)
    }

    const handleGoogleSignIn = async (response: any) => {
        if (response.credential) {
            try {
                await GoogleAuthService.handleGoogleResponse(response, () => {
                    router.reload()
                })
            } catch (error) {
                console.error('Google sign-in failed:', error)
            }
        }
    }

    const normalizeVenmo = (handle: string) => {
        const trimmed = handle.trim()
        if (!trimmed) return null
        return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed
    }

    const handleCreateHousehold = async () => {
        if (submitting) return
        setSubmitting(true)
        try {
            const response = await axiosClient.post('/util/households', {
                household_address: formData.householdAddress,
                household_nickname: formData.householdNickname || null,
                venmo_handle: normalizeVenmo(formData.venmoHandle)
            })
            if (response.data?.success && response.data?.household_id) {
                clearSavedOnboarding()
                router.visit(`/util/dashboard/${response.data.household_id}`)
            }
        } catch (error) {
            console.error('Failed to create household:', error)
            setSubmitting(false)
        }
    }

    const renderStepContent = () => {
        switch (step.id) {
            case 'address':
                return (
                    <GooglePlacesAutocomplete
                        value={formData.householdAddress}
                        onChange={(address) => handleInputChange('householdAddress', address)}
                        textFieldProps={{
                            error: !!errors.householdAddress,
                            helperText: errors.householdAddress,
                            placeholder: 'Enter household address',
                            FormHelperTextProps: { sx: { fontFamily: 'Inter, sans-serif', fontSize: '0.75rem' } }
                        }}
                    />
                )
            case 'nickname':
                return (
                    <TextField
                        value={formData.householdNickname}
                        onChange={(e) => handleInputChange('householdNickname', e.target.value)}
                        placeholder="e.g. The Crib, Casa de Fun"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'black' },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'black' }
                        }}
                    />
                )
            case 'venmo':
                return (
                    <TextField
                        value={formData.venmoHandle}
                        onChange={(e) => handleInputChange('venmoHandle', e.target.value)}
                        placeholder="@username"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'black' },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'black' }
                        }}
                    />
                )
            case 'finish':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Card
                            sx={{
                                p: 2,
                                backgroundColor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 2,
                                width: '100%'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    mb: 2,
                                    color: 'grey.900',
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    textAlign: 'left'
                                }}
                            >
                                {user ? 'Your dashboard is ready!' : 'Almost there!'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'grey.700', fontSize: { xs: '0.95rem', md: '1rem' } }}>
                                {user
                                    ? 'Create your household to start managing utilities.'
                                    : 'Sign in to create your household and open your Utility Dashboard.'}
                            </Typography>
                        </Card>
                    </Box>
                )
            default:
                return null
        }
    }

    if (!isVisible) return null

    return (
        <Box
            id={id}
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                py: { xs: 6, sm: 8 },
                boxSizing: 'border-box',
                borderTop: '1px solid #e5e7eb'
            }}
        >
            <Container maxWidth="md">
                <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            mt: 3,
                            mb: 1,
                            fontSize: { xs: '1.875rem', sm: '2.25rem' },
                            color: 'grey.900'
                        }}
                    >
                        Let&apos;s create your household
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                        sx={{ mb: 4, color: 'grey.700', fontSize: '1.125rem' }}
                    >
                        Tell us about your place so we can set up bill splitting.
                    </Typography>

                    <Card elevation={2} sx={{ borderRadius: 3, p: { xs: 2, sm: 3 }, border: '1px solid #e5e7eb' }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ color: 'grey.600', mb: 1 }}>
                                Step {currentStep + 1} of {STEPS.length}
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 4,
                                    bgcolor: 'grey.200',
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${((currentStep + 1) / STEPS.length) * 100}%`,
                                        height: '100%',
                                        bgcolor: '#fbbf24',
                                        transition: 'width 0.3s ease'
                                    }}
                                />
                            </Box>
                        </Box>

                        {step.id !== 'finish' && (
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        mb: step.subtext ? 1 : 3,
                                        color: 'grey.900'
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                {step.subtext && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'grey.600',
                                            mb: 3,
                                            fontSize: '0.875rem',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        {step.subtext}
                                    </Typography>
                                )}
                                {renderStepContent()}
                            </Box>
                        )}
                        {step.id === 'finish' && renderStepContent()}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 4,
                                gap: { xs: 3, md: 6 }
                            }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBack}
                                disabled={currentStep === 0}
                                sx={{
                                    borderColor: 'grey.300',
                                    color: 'grey.700',
                                    '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' }
                                }}
                            >
                                Back
                            </Button>

                            {step.id === 'finish' ? (
                                !user ? (
                                    <GoogleSignInButton googleCallback={handleGoogleSignIn} />
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleCreateHousehold}
                                        disabled={submitting}
                                        endIcon={<CheckCircleIcon sx={{ color: '#fbbf24' }} />}
                                        sx={{
                                            bgcolor: 'black',
                                            color: 'white',
                                            px: 3,
                                            '&:hover': { bgcolor: 'grey.900', color: 'white' }
                                        }}
                                    >
                                        {submitting ? 'Creating…' : 'Create household'}
                                    </Button>
                                )
                            ) : (
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon sx={{ color: '#fbbf24' }} />}
                                    onClick={handleNext}
                                    sx={{
                                        bgcolor: 'black',
                                        color: 'white',
                                        px: 3,
                                        '&:hover': { bgcolor: 'grey.900', color: 'white' }
                                    }}
                                >
                                    Next
                                </Button>
                            )}
                        </Box>
                    </Card>
                </Box>
            </Container>
        </Box>
    )
}
